import { useCallback, SetStateAction, Dispatch, useMemo } from "react"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { OnBehalfCompletedDialog } from "../../../../shared/components/createOrder/hooks/useCreateOrderUser"
import {
    CreateOrderDialogMode,
    CreateOrderDialogType,
} from "../../../../shared/components/createOrder/createOrderTypes"
import { CreateOrderDialog } from "../../../../shared/components/createOrder/CreateOrderDialog"
import { OutlookBookingSuccessArgs, SessionValue } from "../outlookCreateOrderTypes"
import { CreateOrderType } from "../../../../shared/components/createOrder/helpers"
import {
    CreateOrderDialogFlow,
    CreateOrderDialogFlowMode,
    CreateOrderFlowType,
    CreateOrderOutlookFlowType,
} from "../../../../shared/components/createOrder/createOrderTypes"
import { postBookingResultToOutlook } from "./bookingMessaging"
import { toOutlookUtcFromNaive } from "./toOutlookUtc"
import { isOutlookMeetingValue } from "../helpers"

const useStyles = makeStyles()(() => ({
    wrap: {
        position: "relative",
        height: "100vh",
        overflow: "hidden",
    },
}))

type OutlookCreateOrderProps = Readonly<{
    orderId?: number
    mode: CreateOrderDialogMode
    flowParam: string | null
    modeParam: string | null
    onBehalf: OnBehalfCompletedDialog
    setOnBehalf: Dispatch<SetStateAction<OnBehalfCompletedDialog>>
    parentOrigin: string
    session?: SessionValue[]
}>

export function OutlookCreateOrderContent({
    orderId,
    mode,
    flowParam,
    modeParam,
    onBehalf,
    setOnBehalf,
    parentOrigin,
    session,
}: OutlookCreateOrderProps) {
    const { classes } = useStyles()

    const isOutlookVSTO = flowParam === CreateOrderOutlookFlowType.VSTO

    const isOutlookFlow = useMemo(() => {
        return flowParam === CreateOrderOutlookFlowType.WEB || isOutlookVSTO
    }, [flowParam])

    const flowMode: CreateOrderDialogFlowMode | null = useMemo(() => {
        if (isOutlookVSTO) return CreateOrderDialogFlowMode.MEETING_SERVICE
        if (
            modeParam === CreateOrderDialogFlowMode.MEETING_SERVICE ||
            modeParam === CreateOrderDialogFlowMode.RESOURCE_BOOKING
        ) {
            return modeParam
        }
        return null
    }, [isOutlookFlow, modeParam])

    const sessionOrder = session?.find(isOutlookMeetingValue)?.order

    const flowConfig: CreateOrderDialogFlow | undefined = useMemo(() => {
        if (!isOutlookFlow) return undefined
        return {
            flow: CreateOrderFlowType.OUTLOOK,
            mode: flowMode,
            vstoFlowParam: isOutlookVSTO ? CreateOrderOutlookFlowType.VSTO : undefined,
            order: sessionOrder,
            calendarAppointmentId: sessionOrder?.meetingDetails?.calendarAppointmentId,
        }
    }, [isOutlookVSTO, isOutlookFlow, modeParam, session])

    const activeOrderTab = useMemo(() => {
        return flowMode === CreateOrderDialogFlowMode.MEETING_SERVICE
            ? CreateOrderType.MEETING_SERVICE
            : CreateOrderType.RESOURCE_BOOKING
    }, [flowMode])

    const handleCloseDialog = () => {
        try {
            postBookingResultToOutlook(
                {
                    type: "dialog:close",
                    reason: "manager-dialog-closed-by-user",
                },
                parentOrigin,
            )
        } catch (e) {
            console.error("Failed to post dialog:close to Outlook:", e)
        }
    }

    const handleOutlookBookingCompleted = useCallback(
        async (args: OutlookBookingSuccessArgs) => {
            const { isoUtc: startUtc } = toOutlookUtcFromNaive(args.start)
            const { isoUtc: endUtc } = toOutlookUtcFromNaive(args.end)
            await postBookingResultToOutlook(
                {
                    type: "booking:completed",
                    startUtc,
                    endUtc,
                },
                parentOrigin,
            )
        },
        [parentOrigin],
    )

    return (
        <Grid container className={classes.wrap}>
            <CreateOrderDialog
                open
                orderId={orderId ? +orderId : undefined}
                mode={mode}
                flowConfig={flowConfig}
                onClose={handleCloseDialog}
                activeOrderTab={activeOrderTab}
                handleOutlookBookingCompleted={handleOutlookBookingCompleted}
                isOnBehalfCompleted={onBehalf}
                setIsOnBehalfCompleted={setOnBehalf}
                dialogType={CreateOrderDialogType.ORDER}
            />
        </Grid>
    )
}
